import type { ExploreItem } from 'tripPlanner/types/common'

type PlaceSummary = NonNullable<ExploreItem['generativeSummary']>

export interface PlaceDescription {
  text: string
  /** Google requires both to be shown with an AI summary, so they travel together or not at all. */
  attribution?: { disclosureText: string; flagContentUri: string }
}

function isHttpsUrl(value: string): boolean {
  try {
    return new URL(value).protocol === 'https:'
  } catch {
    return false
  }
}

function toAttributedDescription(summary: PlaceSummary | undefined): PlaceDescription | undefined {
  // The report link becomes an href, so anything that is not a real https URL is not renderable.
  if (!summary?.text || !summary.disclosureText || !summary.flagContentUri) return undefined
  if (!isHttpsUrl(summary.flagContentUri)) return undefined

  return {
    text: summary.text,
    attribution: {
      disclosureText: summary.disclosureText,
      flagContentUri: summary.flagContentUri,
    },
  }
}

/**
 * Google's AI overview is US-only; its editorial line and ours describe the place, so the review
 * summary reads last — it is what visitors thought, not what the place is.
 */
export function getPlaceDescription(place: ExploreItem): PlaceDescription | undefined {
  const { generativeSummary, editorialSummary, generatedSummary, reviewSummary } = place

  return toAttributedDescription(generativeSummary) ??
    (editorialSummary ? { text: editorialSummary } : undefined) ??
    (generatedSummary ? { text: generatedSummary } : undefined) ??
    toAttributedDescription(reviewSummary)
}

/** For surfaces with no room for attribution, which therefore may not use the AI summaries. */
export function getUnattributedPlaceDescription(place: ExploreItem): string | undefined {
  return place.editorialSummary ?? place.generatedSummary
}
